/**
 * prompts.js - turning a support rung into something Pip can say and the room
 * can show.
 *
 * PURE. The words are content/voice.js; which words, the engine's rung. Line
 * SELECTION is injected (`choose`) so the anti-repeat bag stays in one place
 * and this file stays testable without a React render.
 */

import { rungIndexOf, rungIdAt } from '../../shared/support.js';
import * as VOICE from '../content/voice.js';
import { getNearby, canonicalPhrase } from '../content/nearby.js';

/**
 * Templates for a rung, falling back toward MORE support when the phase does
 * not define the exact one. More help than the rung intended costs a box; a
 * mute Pip costs the trial.
 *
 * At Phase 1 there is only ever `full_model`, so the fallback never fires here.
 * It is kept because the fallback is the contract's, not this phase's, and a
 * sibling level at another phase would need it.
 */
export function templatesFor(phase, rungId) {
  const byRung = VOICE.PROMPTS[phase] || {};
  if (byRung[rungId] && byRung[rungId].length) return byRung[rungId];
  for (let index = rungIndexOf(rungId) - 1; index >= 0; index -= 1) {
    const id = rungIdAt(index);
    if (byRung[id] && byRung[id].length) return byRung[id];
  }
  const any = Object.values(byRung).find((lines) => lines && lines.length);
  return any || ['Your turn.'];
}

/**
 * What is drawn on the card at this rung, if anything.
 *
 * Phase 1 opens at `full_model` and cannot leave it, so in this game the answer
 * is always the word itself - which is the phase, not a leak: section 8.2's
 * Phase 1 mustInclude is that Pip says the word aloud.
 */
export function cueFor({ rungId, cueStyle, objectId }) {
  const one = getNearby(objectId);
  if (!one) return null;
  if (rungId === 'full_model') return { kind: 'word', text: one.word };
  if (rungId === 'partial_verbal') {
    const head = one.firstLetter || one.word[0].toUpperCase();
    if (cueStyle === 'partial_word') {
      return {
        kind: 'partial_word',
        text: `${head}${'_'.repeat(Math.max(2, one.word.length - head.length))}`,
      };
    }
    if (cueStyle === 'first_letter') return { kind: 'first_letter', text: head };
  }
  return null;
}

/**
 * @returns {{text:string, cue:object|null, chips:null, showsAnswer:boolean}}
 */
export function buildPrompt({ phase, rungId, target, content, choose }) {
  const one = getNearby(target.objectId);

  const text = VOICE.renderLine(choose(templatesFor(phase, rungId)), {
    word: one.word,
    clue: one.clue,
  });

  return {
    text,
    cue: cueFor({ rungId, cueStyle: content.cueStyle, objectId: target.objectId }),
    // Does this prompt hand the child the answer? Drives the record's
    // modelPlayed. At Phase 1 it is true on every trial, by design.
    showsAnswer: rungId === 'full_model',
    // Word frames are a Phase 3 idea. Nothing is offered on screen here.
    chips: null,
  };
}

/** Pip's clue. It must never contain its own answer - the check is in content/. */
export function buildHint({ phase, target, choose }) {
  const one = getNearby(target.objectId);
  return VOICE.renderLine(choose(VOICE.HINTS[phase] || VOICE.HINTS[1]), {
    clue: one.clue,
  });
}

/** The phrase Pip models, exposed so the component and the loop agree on it. */
export { canonicalPhrase };

export default { templatesFor, cueFor, buildPrompt, buildHint };
